import React, { useState, useMemo } from 'react';
import { SlidersHorizontal, X, Search } from 'lucide-react';

const FUEL_TYPES = ['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'];
const TRANSMISSIONS = ['Manual', 'Automatic'];
const BODY_TYPES = ['Hatchback', 'Sedan', 'SUV', 'MUV', 'Coupe', 'Luxury'];

const BUDGET_RANGES = [
  { label: 'Under ₹2L', min: 0, max: 200000 },
  { label: '₹2L - ₹5L', min: 200000, max: 500000 },
  { label: '₹5L - ₹10L', min: 500000, max: 1000000 },
  { label: '₹10L - ₹20L', min: 1000000, max: 2000000 },
  { label: 'Over ₹20L', min: 2000000, max: Infinity },
];

const KM_RANGES = [
  { label: '< 25k km', min: 0, max: 25000 },
  { label: '25k - 50k km', min: 25000, max: 50000 },
  { label: '50k - 100k km', min: 50000, max: 100000 },
  { label: '> 100k km', min: 100000, max: Infinity },
];

const YEAR_RANGES = (() => {
  const now = new Date().getFullYear();
  return [
    { label: `${now - 2}+`, min: now - 2, max: now },
    { label: `${now - 5} - ${now - 3}`, min: now - 5, max: now - 3 },
    { label: `${now - 10} - ${now - 6}`, min: now - 10, max: now - 6 },
    { label: `Older than ${now - 10}`, min: 0, max: now - 11 },
  ];
})();

export const useListingFilters = (listings) => {
  const [filters, setFilters] = useState({
    fuel: [], budget: [], year: [], km: [], transmission: [], body: [],
  });
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    // Whitespace-tolerant AND search across make / model / year / fuel /
    // transmission / body / description. Every token in the query must
    // appear in the joined haystack (case-insensitive).
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return listings.filter((l) => {
      if (filters.fuel.length && !filters.fuel.includes(l.fuel_type)) return false;
      if (filters.transmission.length && !filters.transmission.includes(l.ai_details?.transmission)) return false;
      if (filters.body.length && !filters.body.includes(l.ai_details?.body_type)) return false;
      if (filters.budget.length) {
        const ok = filters.budget.some((label) => {
          const r = BUDGET_RANGES.find((x) => x.label === label);
          return r && l.price >= r.min && l.price < r.max;
        });
        if (!ok) return false;
      }
      if (filters.km.length) {
        const ok = filters.km.some((label) => {
          const r = KM_RANGES.find((x) => x.label === label);
          return r && (l.km_driven || 0) >= r.min && (l.km_driven || 0) < r.max;
        });
        if (!ok) return false;
      }
      if (filters.year.length) {
        const ok = filters.year.some((label) => {
          const r = YEAR_RANGES.find((x) => x.label === label);
          return r && l.year >= r.min && l.year <= r.max;
        });
        if (!ok) return false;
      }
      if (tokens.length) {
        const hay = [
          l.ai_details?.make, l.ai_details?.model, l.ai_details?.variant,
          l.ai_details?.body_type, l.ai_details?.transmission,
          l.fuel_type, l.year, l.description,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!tokens.every((t) => hay.includes(t))) return false;
      }
      return true;
    });
  }, [listings, filters, query]);

  const toggle = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((v) => v !== value) : [...prev[key], value],
    }));
  };

  const clearAll = () => {
    setFilters({ fuel: [], budget: [], year: [], km: [], transmission: [], body: [] });
    setQuery('');
  };

  const activeCount = Object.values(filters).reduce((sum, arr) => sum + arr.length, 0)
    + (query.trim() ? 1 : 0);

  return { filters, filtered, toggle, clearAll, activeCount, query, setQuery };
};

const FilterGroup = ({ title, options, selected, onToggle, testid, dark }) => (
  <div data-testid={testid}>
    <h3 className={`font-mono-tech text-[10px] uppercase tracking-[0.28em] mb-3 ${dark ? 'text-slate-500' : 'text-zinc-400'}`}>
      {title}
    </h3>
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const value = typeof opt === 'string' ? opt : opt.label;
        const isOn = selected.includes(value);
        const onCls = dark
          ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0C0E12]'
          : 'bg-[#0A3622] border-[#0A3622] text-white';
        const offCls = dark
          ? 'bg-transparent border-[#272E3D] text-slate-300 hover:border-[#D4AF37]/60'
          : 'bg-white border-zinc-200 text-zinc-700 hover:border-[#0A3622]';
        return (
          <button
            key={value}
            onClick={() => onToggle(value)}
            data-testid={`filter-${title.toLowerCase().replace(/\s+/g, '-')}-${value.toLowerCase().replace(/\s+/g, '-')}`}
            className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider border transition-colors ${isOn ? onCls : offCls}`}
          >
            {value}
          </button>
        );
      })}
    </div>
  </div>
);

const ListingFilters = ({ filters, toggle, clearAll, activeCount, total, filteredCount, dark = false, query = '', setQuery }) => {
  const [open, setOpen] = useState(false);

  const wrapperCls = dark
    ? 'bg-[#14171D] border border-[#272E3D] text-slate-200'
    : 'bg-white border border-zinc-200 text-zinc-900';

  const accentText = dark ? 'text-[#D4AF37]' : 'text-[#0A3622]';
  const accentBg = dark ? 'bg-[#D4AF37] text-[#0C0E12]' : 'bg-[#0A3622] text-white';

  const searchInputCls = dark
    ? 'bg-[#0C0E12] border border-[#272E3D] text-white placeholder-slate-500 focus:border-[#D4AF37]/60'
    : 'bg-zinc-50 border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:border-[#0A3622]';

  const searchBox = setQuery ? (
    <div className="relative">
      <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${dark ? 'text-slate-500' : 'text-zinc-400'}`} />
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search make, model, year…"
        data-testid="listing-search-input"
        className={`w-full pl-9 pr-8 py-2 text-sm rounded-sm outline-none transition-colors ${searchInputCls}`}
      />
      {query && (
        <button
          type="button"
          onClick={() => setQuery('')}
          aria-label="Clear search"
          data-testid="listing-search-clear"
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded ${dark ? 'text-slate-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  ) : null;

  const groups = (
    <div className="space-y-7">
      <FilterGroup dark={dark} title="Fuel" options={FUEL_TYPES} selected={filters.fuel} onToggle={(v) => toggle('fuel', v)} testid="filter-group-fuel" />
      <FilterGroup dark={dark} title="Budget" options={BUDGET_RANGES} selected={filters.budget} onToggle={(v) => toggle('budget', v)} testid="filter-group-budget" />
      <FilterGroup dark={dark} title="Year" options={YEAR_RANGES} selected={filters.year} onToggle={(v) => toggle('year', v)} testid="filter-group-year" />
      <FilterGroup dark={dark} title="KM Driven" options={KM_RANGES} selected={filters.km} onToggle={(v) => toggle('km', v)} testid="filter-group-km" />
      <FilterGroup dark={dark} title="Transmission" options={TRANSMISSIONS} selected={filters.transmission} onToggle={(v) => toggle('transmission', v)} testid="filter-group-transmission" />
      <FilterGroup dark={dark} title="Body Type" options={BODY_TYPES} selected={filters.body} onToggle={(v) => toggle('body', v)} testid="filter-group-body" />
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className={`hidden lg:block sticky top-8 self-start p-7 ${wrapperCls}`}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-base font-bold uppercase tracking-[0.18em] flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {activeCount > 0 && <span className={`text-[10px] font-mono-tech px-2 py-0.5 ${accentBg}`}>{activeCount}</span>}
          </h2>
          {activeCount > 0 && (
            <button onClick={clearAll} className={`text-[10px] uppercase tracking-[0.2em] font-bold ${accentText} hover:underline`} data-testid="filters-clear">
              Clear
            </button>
          )}
        </div>
        <div className={`font-mono-tech text-[10px] uppercase tracking-[0.22em] mb-5 ${dark ? 'text-slate-500' : 'text-zinc-500'}`}>
          <span className={dark ? 'text-white font-bold' : 'text-zinc-900 font-bold'}>{filteredCount}</span>
          <span className="opacity-60"> / {total} cars</span>
        </div>
        {searchBox && <div className="mb-6">{searchBox}</div>}
        {groups}
      </aside>

      {/* Mobile button + drawer */}
      <div className="lg:hidden">
        <button
          onClick={() => setOpen(true)}
          data-testid="mobile-filter-btn"
          className={`inline-flex items-center gap-2 px-4 py-2.5 font-bold uppercase tracking-[0.18em] text-xs ${dark ? 'bg-[#14171D] border border-[#272E3D] text-slate-200' : 'bg-white border border-zinc-200 text-zinc-900'}`}
        >
          <SlidersHorizontal className="w-4 h-4" /> Filters
          {activeCount > 0 && <span className={`text-[10px] font-mono-tech px-2 py-0.5 ${accentBg}`}>{activeCount}</span>}
        </button>
        {open && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
            <div
              className={`fixed bottom-0 inset-x-0 max-h-[85vh] overflow-y-auto p-6 ${dark ? 'bg-[#0C0E12] border-t border-[#272E3D]' : 'bg-white border-t border-zinc-200'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-lg font-bold uppercase tracking-[0.18em]">
                  Filters {activeCount > 0 && <span className={`text-[10px] font-mono-tech px-2 py-0.5 ml-2 ${accentBg}`}>{activeCount}</span>}
                </h2>
                <button onClick={() => setOpen(false)} className={`p-2 ${dark ? 'hover:bg-white/10' : 'hover:bg-zinc-100'}`}><X className="w-5 h-5" /></button>
              </div>
              {searchBox && <div className="mb-5">{searchBox}</div>}
              {groups}
              <div className={`sticky bottom-0 pt-5 mt-7 flex gap-2 border-t ${dark ? 'border-[#272E3D] bg-[#0C0E12]' : 'border-zinc-200 bg-white'}`}>
                {activeCount > 0 && (
                  <button onClick={clearAll} className={`flex-1 py-3 font-bold text-xs uppercase tracking-[0.18em] ${dark ? 'border border-[#272E3D] text-slate-200' : 'border border-zinc-300 text-zinc-700'}`}>
                    Clear ({activeCount})
                  </button>
                )}
                <button onClick={() => setOpen(false)} className={`flex-1 py-3 font-bold text-xs uppercase tracking-[0.18em] ${accentBg}`}>
                  Show {filteredCount} cars
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ListingFilters;
